import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Gift, Sparkles } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const KEY = "ud-survey";
const SNOOZE_KEY = "ud-survey-snooze";
const SNOOZE_MS = 7 * 24 * 3600 * 1000;

export const SurveyPopup = () => {
  const { homepage } = useSettings();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (homepage.survey_enabled === false) return;
    if (pathname.startsWith("/admin") || pathname.startsWith("/publish") || pathname.startsWith("/survey") || pathname.startsWith("/reset-password") || pathname.startsWith("/forgot-password")) return;
    const state = localStorage.getItem(KEY);
    if (state === "done" || state === "never") return;
    const snoozed = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (snoozed > Date.now()) return;
    const t = setTimeout(() => setOpen(true), 2500);
    return () => clearTimeout(t);
  }, [homepage.survey_enabled, pathname]);

  const later = () => { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)); setOpen(false); };
  const never = () => { localStorage.setItem(KEY, "never"); setOpen(false); };
  if (!open) return null;

  const prize = homepage.survey_prize || "$100";
  return (
    <div className="fixed bottom-5 right-5 left-5 sm:left-auto sm:w-[380px] z-[60] reveal" role="dialog" aria-label="Listener survey" data-testid="survey-popup">
      <div className="relative overflow-hidden rounded-3xl border border-sky-400/30 bg-[#071A38] shadow-2xl shadow-black/50">
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-sky-400/25 blur-3xl" />
        <div className="absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-rose-500/20 blur-3xl" />
        <button onClick={later} className="absolute top-3 right-3 z-10 rounded-full p-1.5 text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Close" data-testid="survey-popup-close"><X size={16} /></button>
        <div className="relative p-6">
          <div className="flex items-start gap-4">
            <div className="relative shrink-0">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-400 to-sky-600 text-[#070F1E] shadow-lg shadow-sky-500/30 rotate-[-6deg]"><Gift size={30} strokeWidth={2.2} /></span>
              <span className="absolute -bottom-2 -right-3 rounded-full bg-rose-500 px-2 py-0.5 font-display text-xs font-black text-white shadow" data-testid="survey-popup-prize">WIN {prize}</span>
            </div>
            <div className="min-w-0">
              <p className="eyebrow flex items-center gap-1.5 text-sky-300"><Sparkles size={12} /> Listener survey</p>
              <h3 className="mt-1 font-display text-lg font-extrabold leading-snug" data-testid="survey-popup-headline">{homepage.survey_headline || `Take our listener survey for the chance to win ${prize}`}</h3>
              <p className="mt-1 text-xs text-slate-400">About 5 minutes. Help shape the shows you love.</p>
            </div>
          </div>
          <Link to="/survey" onClick={() => setOpen(false)} className="mt-5 flex w-full items-center justify-center rounded-full bg-sky-400 py-2.5 text-sm font-bold text-[#070F1E] hover:bg-sky-300 transition-colors" data-testid="survey-popup-cta">Take the survey</Link>
          <div className="mt-3 flex items-center justify-between text-xs">
            <button onClick={later} className="text-slate-400 hover:text-white" data-testid="survey-popup-later">Maybe later</button>
            <button onClick={never} className="text-slate-500 hover:text-slate-300 underline-offset-2 hover:underline" data-testid="survey-popup-never">Never show me this again</button>
          </div>
        </div>
      </div>
    </div>
  );
};
